import {
  Box,
  DialogContentText,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  Switch,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import * as React from "react";
import {ReactElement, useEffect, useState} from "react";
import TextFieldValidator from "../../../components/form/TextFieldValidator";
import {CreateAssetConversionInput, UpdateAssetConversionInput, UpdateAssetInput} from "../../../API";
import {useResources} from "../../../stores/ResourceProvider";
import Model from "../../../model/Model";
import FormGroupSpacer from "../../../components/form/FormGroupSpacer";
import Asset, {AssetCategory, AssetType, AssetTypeDef, AssetTypeDefs, getAssetTypeDef} from "../../../model/Asset";
import ModelEditDialog from "../../../components/model/ModelEditDialog";
import AssetConversion, {AssetConversionType} from "../../../model/AssetConversion";
import {
  getISODateToday,
  humanizeString,
  isoToLocalDateString,
  moneyToNumberFormat,
  numberToMoneyFormat,
  numberToPercentFormat
} from "../../../stores/StoreUtilities";
import {getIncomeTypeDef, IncomeType} from "../../../model/Income";
import Visible from "../../../components/Visible";
import LockIcon from "@mui/icons-material/Lock";
import {isNumber} from "../../../model/ModelUtilities";
import ChatbotCloseImg from "../../../images/Chatbot.png";
import ChatbotOpenImg from "../../../images/ChatbotOpen.png";
import {addMonths} from "date-fns";

const AssetConversionEditDialog = ({
  open,
  assetConversion,
  model,
  onClose,
  onSave,
  onDelete,
  onArchive,
  onCommit
}: {
  open? : boolean
  assetConversion: AssetConversion
  model: Model
  onClose?(): void
  onSave?(update: AssetConversion): void
  onDelete?(item: AssetConversion): void
  onArchive?(item: AssetConversion): void
  onCommit?(results: Array<Asset | AssetConversion | undefined>): void
}) => {
  const [assetConversionModel, setAssetConversionModel] = useState<AssetConversion>(assetConversion)
  const [description, setDescription] = useState<string>("")
  const [conversionType, setConversionType] = useState<AssetConversionType>(AssetConversionType.OtherConversion)
  const [srcAsset, setSrcAsset] = useState<Asset | undefined>()
  const [dstAsset, setDstAsset] = useState<Asset | undefined>()
  const [srcAssets, setSrcAssets] = useState<Asset[]>([])
  const [dstAssets, setDstAssets] = useState<Asset[]>([])
  const [year, setYear] = useState<number | undefined>()
  const [completedAt, setCompletedAt] = useState<string | undefined>()
  const [amount, setAmount] = useState<string | undefined>()
  const [isAmount, setIsAmount] = useState<boolean>(true)
  const [percent, setPercent] = useState<string | undefined>()
  const [incomeType, setIncomeType] = useState<IncomeType | undefined>()
  const [nonTaxableAmount, setNonTaxableAmount] = useState<string | undefined>()
  const [commit, setCommit] = useState<boolean>(false)
  const [archived, setArchived] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isLocked, setIsLocked] = useState<boolean>(false)
  const [showHelp, setShowHelp] = useState<boolean>(false)

  const { modelStore, notify, confirm } = useResources()
  const theme = useTheme()

  const styles = {
    halfWidth: {
      width: "50%"
    },
    halfWidthAutoComplete: {
      width: 270,
      paddingTop: "8px"
    },
    radio: {
      paddingTop: 0,
      paddingLeft: 0
    },
    helpText: {
      fontSize: 14,
      lineHeight: 1.4
    }
  }

  const incomeTypes: IncomeType[] = [
    IncomeType.None,
    IncomeType.WagesSalariesTips,
    IncomeType.LongTermCapitalGainsOrLoss,
    IncomeType.ShortTermCapitalGainsOrLoss,
    IncomeType.TaxableIRADistribution,
    IncomeType.OtherIncome
  ]

  useEffect(() => {
    setAssetConversionModel(assetConversion)
    setDescription(assetConversion.description)
    setConversionType(assetConversion.conversionType)
    if (assetConversion.amount > 0 || !assetConversion.percent) {
      setIsAmount(true)
      setAmount(numberToMoneyFormat(assetConversion.amount, 0))
      setPercent(isNumber(assetConversion.percent) ? numberToPercentFormat(assetConversion.percent * 100, 0, 'decimal')  : "0")
    } else {
      setIsAmount(false)
      setPercent(isNumber(assetConversion.percent) ? numberToPercentFormat(assetConversion.percent * 100, 0, 'decimal') : "100")
      setAmount(numberToMoneyFormat(0, 0))
    }
    const src = model.getAssetById(assetConversion.srcAssetId)
    setSrcAsset(src)
    const dst = model.getAssetById(assetConversion.dstAssetId)
    setDstAsset(dst)
    setYear(assetConversion.year ?? undefined)
    setCompletedAt(assetConversion.completedAt ? isoToLocalDateString(assetConversion.completedAt) : undefined)
    // TODO: Update with new field
    setNonTaxableAmount(numberToMoneyFormat(assetConversion.nonTaxableAmount, 0))
    setIncomeType(assetConversion.incomeType)
    setArchived(assetConversion.archived)
    setIsOpen(open === true)
    setIsLocked(model.hasLock(assetConversion.id, "*") || model.isLocked())
  }, [assetConversion, model, open])

  useEffect(() => {
    if (conversionType === AssetConversionType.RothConversion) {
      const assetTypes = AssetTypeDefs.filter((at: AssetTypeDef) => at.convertableTo.includes(AssetType.RothIRA)).map((at: AssetTypeDef) => at.assetType)
      const rothTypes: AssetType[] = [AssetType.RothIRA, AssetType.RothTSP, AssetType.Roth401k]

      setSrcAssets(model.assets.filter((a: Asset) => assetTypes.includes(a.assetType) && !a.archived))
      setDstAssets(model.assets.filter((a: Asset) => rothTypes.includes(a.assetType) && !a.archived))
      if (srcAsset && !srcAsset.assetTypeDef.convertableTo.includes(AssetType.RothIRA)) {
        setSrcAsset(undefined)
      }
      if (dstAsset && !rothTypes.includes(dstAsset.assetType)) {
        setDstAsset(undefined)
      }
    } else if (conversionType === AssetConversionType.QCD) {
      const assetTypes = AssetTypeDefs.filter((at: AssetTypeDef) => at.qcdAllowed).map((at: AssetTypeDef) => at.assetType)
      setSrcAssets(model.assets.filter((a: Asset) => assetTypes.includes(a.assetType) && !a.archived))
      if (srcAsset && !srcAsset.assetTypeDef.hasRMD) {
        setSrcAsset(undefined)
      }
      setDstAssets([])
    } else {
      // Allow both Liquid Assets and Other Assets
      setSrcAssets(model.assets.filter((a: Asset) => (a.assetCategory === AssetCategory.LiquidInvestableAssets || a.assetCategory === AssetCategory.RealEstateAndProperty || a.assetCategory === AssetCategory.OtherAssets) && !a.archived))
      setDstAssets(model.assets.filter((a: Asset) => a.assetCategory === AssetCategory.LiquidInvestableAssets && !a.archived))
    }
  }, [conversionType])

  const handleClose = async (event: any) => {
    if (onClose) {
      onClose()
    }
  }

  const handleSave = async (event: any) => {
    try {
      const isQCD = conversionType === AssetConversionType.QCD
      const pct = percent ? parseInt(percent) / 100 : 0
      let amt = moneyToNumberFormat(amount, 0) ?? 0
      const nonTaxableAmt = moneyToNumberFormat(nonTaxableAmount, 0) ?? 0
      if (commit) {
        const isoDate = getISODateToday()
        if (srcAsset && (isQCD || dstAsset)) {
          if (isAmount && srcAsset.balance < amt) {
            notify.show("error", `Insufficient balance of ${numberToMoneyFormat(srcAsset.balance)} in ${srcAsset.description}`)
            return
          }
          if (isAmount && nonTaxableAmt > amt) {
            notify.show("error", "Tax Excludes amount cannot exceed Amount")
            return
          }
          const promises: Promise<Asset | AssetConversion | undefined>[] = []
          if (!isAmount) {
            if (isQCD) {
              notify.show("error", "QCD must be converted to a fixed amount to Complete")
              return
            }
            amt = srcAsset.balance * pct
          }
          const srcUpdate: UpdateAssetInput = {
            id: srcAsset.id,
            accountId: srcAsset.accountId,
            balance: Math.max(srcAsset.balance - amt, 0),
            balanceDate: isoDate
          }
          if (srcAsset.assetType === AssetType.Brokerage && srcAsset.assetIncome && isNumber(srcAsset.assetIncome.basis)) {
            // Decrease basis
            srcUpdate.assetIncome = srcAsset.assetIncome
            srcUpdate.assetIncome.basis = Math.max(srcAsset.assetIncome.basis! - amt, 0)
          }
          if (srcAsset.assetCategory === AssetCategory.RealEstateAndProperty && srcUpdate.balance === 0) {
            // End the real estate asset if balance is zero
            srcUpdate.end = getISODateToday()
          }
          promises.push(modelStore.updateAsset(srcUpdate))

          if (conversionType !== AssetConversionType.QCD && dstAsset) {
            const dstUpdate: UpdateAssetInput = {
              id: dstAsset.id,
              accountId: dstAsset?.accountId,
              balance: dstAsset.balance + amt,
              balanceDate: isoDate
            }
            if (dstAsset.assetType === AssetType.Brokerage && dstAsset.assetIncome && isNumber(dstAsset.assetIncome.basis)) {
              // Increase basis
              dstUpdate.assetIncome = dstAsset.assetIncome
              dstUpdate.assetIncome.basis = Math.max(dstAsset.assetIncome.basis! + amt, 0)
            }
            promises.push(modelStore.updateAsset(dstUpdate))
          }

          if (!assetConversionModel.createdAt) {
            const input: CreateAssetConversionInput = {
              id: assetConversionModel.id,
              accountId: assetConversionModel.accountId,
              userId: assetConversionModel.userId,
              modelId: assetConversionModel.modelId,
              description: description,
              conversionType: conversionType,
              srcAssetId: srcAsset!.id,
              dstAssetId: dstAsset ? dstAsset!.id : "",
              year: year ?? 0,
              amount: amt, // Always save amount when committed
              percent: !isAmount ? pct : 0,
              sortOrder: assetConversionModel.sortOrder,
              completedAt: isoDate,
              nonTaxableAmount: nonTaxableAmt,
              incomeType: incomeType
            }

            promises.push(modelStore.createAssetConversion(input))
          } else {
            const input: UpdateAssetConversionInput = {
              id: assetConversionModel.id,
              accountId: assetConversionModel.accountId,
              userId: assetConversionModel.userId,
              modelId: assetConversionModel.modelId,
              description: description,
              conversionType: conversionType,
              srcAssetId: srcAsset!.id,
              dstAssetId: dstAsset ? dstAsset!.id : "",
              year: new Date().getFullYear(),
              amount: amt, // Always save amount when committed
              percent: !isAmount ? pct : 0,
              completedAt: isoDate,
              nonTaxableAmount: nonTaxableAmt,
              incomeType: incomeType
            }
            promises.push(modelStore.updateAssetConversion(input))
          }

          const results = await Promise.all(promises)

          notify.show("success", "Conversion completed")
          if (onCommit) {
            onCommit(results)
          }
        }
      } else {
        let updated: AssetConversion | undefined

        if (!assetConversionModel.createdAt) {
          const input: CreateAssetConversionInput = {
            id: assetConversionModel.id,
            accountId: assetConversionModel.accountId,
            userId: assetConversionModel.userId,
            modelId: assetConversionModel.modelId,
            description: description,
            conversionType: conversionType,
            srcAssetId: srcAsset!.id,
            dstAssetId: dstAsset ? dstAsset!.id : "",
            year: year ?? 0,
            amount: isAmount ? amt : 0,
            percent: !isAmount ? pct : 0,
            sortOrder: assetConversionModel.sortOrder,
            nonTaxableAmount: moneyToNumberFormat(nonTaxableAmount, 0) ?? 0,
            incomeType: incomeType
          }

          updated = await modelStore.createAssetConversion(input)
        } else {
          const input: UpdateAssetConversionInput = {
            id: assetConversionModel.id,
            accountId: assetConversionModel.accountId,
            userId: assetConversionModel.userId,
            modelId: assetConversionModel.modelId,
            description: description,
            conversionType: conversionType,
            srcAssetId: srcAsset!.id,
            dstAssetId: dstAsset ? dstAsset!.id : "",
            year: year ?? 0,
            amount: isAmount ? amt : 0,
            percent: !isAmount ? pct : 0,
            nonTaxableAmount: moneyToNumberFormat(nonTaxableAmount, 0) ?? 0,
            incomeType: incomeType
          }

          updated = await modelStore.updateAssetConversion(input)
        }

        if (onSave && updated) {
          onSave(updated)
        } else if (onClose) {
          onClose()
        }
      }
    } catch (err: any) {
      notify.show('error', err.message)
    }
  }

  const handleArchive = async () => {
    if (onArchive) {
      if (assetConversionModel.createdAt) {
        const assetConversion = await modelStore.updateAssetConversion({id: assetConversionModel.id, archived: !assetConversionModel.archived})
        if (assetConversion) {
          onArchive(assetConversion)
        }
      } else if (onDelete) {
        onDelete(assetConversionModel)
      }
    }
  }

  const handleDelete = async () => {
    if (onDelete) {
      if (assetConversionModel.createdAt) {
        const message = (assetConversion.completedAt) ?
          `This record of the conversion may be required to calculate taxes. It will also reverse this conversion.` :
          `Are you sure you want to delete this conversion?`
        confirm.show('Confirm Delete', message,
          ['Confirm', 'Cancel'],
          async () => {
            try {
              if (assetConversion.completedAt) {
                const srcAsset = model.getAssetById(assetConversion.srcAssetId)
                if (srcAsset) {
                  const update: UpdateAssetInput = {
                    id: srcAsset.id,
                    balance: srcAsset.balance + assetConversion.amount,
                    balanceDate: getISODateToday()
                  }
                  await modelStore.updateAsset(update)
                }

                const dstAsset = assetConversion.dstAssetId ? model.getAssetById(assetConversion.dstAssetId) : undefined
                if (dstAsset) {
                  const update: UpdateAssetInput = {
                    id: dstAsset.id,
                    balance: Math.max(dstAsset.balance - assetConversion.amount, 0),
                    balanceDate: getISODateToday()
                  }
                  await modelStore.updateAsset(update)
                }
              }

              const deleted = await modelStore.deleteAssetConversion(assetConversionModel.id)
              if (deleted) {
                onDelete!(deleted)
              }
              return true
            }
            catch (err: any) {
              notify.show('error', err.message)
              return false
            }
          })
       } else {
        onDelete(assetConversionModel)
      }
    }
  }

  const getMinYear = (): number => {
    let year = (new Date()).getFullYear()
    if (conversionType === AssetConversionType.QCD && srcAsset) {
      if (srcAsset.owner) {
        let minDate = addMonths(srcAsset.owner.getDateOnAge(70), 6)
        year = Math.max(minDate.getFullYear(), year)
      }
    }
    return year
  }

  const minYear: number = getMinYear()
  const disabled = completedAt !== undefined || isLocked

  const getDestinations = (srcAsset?: Asset): Asset[] => {
    let assetTypes: AssetType[] = []
    let assets: Asset[] = []

    if (srcAsset) {
      if (srcAsset.assetCategory === AssetCategory.LiquidInvestableAssets) {
        assetTypes = srcAsset.assetTypeDef.convertableTo
      } else if (srcAsset.assetCategory === AssetCategory.RealEstateAndProperty) {
        assetTypes = [AssetType.Brokerage, AssetType.Checking, AssetType.Savings, AssetType.MoneyMarket]
      } else if (srcAsset.assetCategory === AssetCategory.OtherAssets) {
        // Only allow taxable accounts, use Brokerage for the list
        assetTypes = getAssetTypeDef(AssetType.Brokerage).convertableTo
      }

      assets = dstAssets.filter((a: Asset) => a.id !== srcAsset.id && assetTypes.findIndex((at: AssetType) => at === a.assetType) >= 0)
      Model.sortAssets(assets)
    } else {
      assets = dstAssets
    }

    return assets
  }

  const renderAmountLabel = () => {
    return (
      <Box display="inline-block">
        <Radio checked={isAmount} sx={styles.radio}
               onChange={(event: any) => {
                 setIsAmount(event.target.checked)
               }}/>
        <span style={{marginRight: "16px"}}>Amount</span>
        <Radio checked={!isAmount} sx={styles.radio} onChange={(event: any) => setIsAmount(!event.target.checked)}/>
        <span>{conversionType === AssetConversionType.QCD ? "% of RMD" : "%"}</span>
      </Box>
    )
  }

  const renderHelp = () => {
    let help: ReactElement
    if (conversionType === AssetConversionType.RothConversion) {
      help = <Box sx={{display:"flex", flexDirection:"column", flexGrow:1, ml:1,  bgcolor:"#f1f1f2", borderBottomLeftRadius:20, borderTopRightRadius:20, borderBottomRightRadius:20, padding:2}}>
        <p style={styles.helpText}>A <b>Roth conversion</b> involves transferring funds from a <b>Traditional IRA</b> or other retirement accounts into a <b>Roth IRA</b>. The primary benefit of a Roth IRA is that it allows for tax-free withdrawals in retirement, provided certain conditions are met. However, when you perform a Roth conversion, the amount converted is considered taxable income for that year, which could impact your tax situation.</p>
        <p style={styles.helpText}>Roth conversions can be a strategic move for some individuals, especially if they anticipate being in a higher tax bracket in the future or if they want to take advantage of the tax-free growth and withdrawals that a Roth IRA offers. It's important to carefully consider the tax implications and consult with a financial advisor or tax professional to determine if a Roth conversion aligns with your retirement goals.</p>
      </Box>
    } else if (conversionType === AssetConversionType.QCD) {
      help = <Box sx={{display:"flex", flexDirection:"column", flexGrow:1, ml:1,  bgcolor:"#f1f1f2", borderBottomLeftRadius:20, borderTopRightRadius:20, borderBottomRightRadius:20, padding:2}}>
        <p style={styles.helpText}>A <b>Qualified Charitable Distribution (QCD)</b> is a direct transfer of funds from an <b>IRA (Individual Retirement Account)</b> to a <b>qualified 501(c)(3) charity</b>. It allows <b>individuals aged 70½ or older</b> to donate up to <b>$100,000 per year</b> tax-free.</p>
        <p style={styles.helpText}>Key benefits and rules:</p>
        <ul style={styles.helpText}>
          <li>The <b>donation is excluded from taxable income</b>, helping reduce overall tax liability.</li>
          <li><b>Counts toward Required Minimum Distributions (RMDs)</b> for individuals 73 and older.</li>
          <li>Must be made directly from an <b>IRA (not a 401(k) or other retirement account)</b>.</li>
          <li>The recipient must be a <b>qualified charity</b> (private foundations and donor-advised funds do not qualify).</li>
          <li>The donor <b>cannot claim a charitable deduction</b> for the contribution.</li>
        </ul>
        <p style={styles.helpText}>QCDs are a tax-efficient way for retirees to support charitable causes while managing their taxable income.</p>
      </Box>
    } else {
      help = <Box sx={{display:"flex", flexDirection:"column", flexGrow:1, ml:1,  bgcolor:"#f1f1f2", borderBottomLeftRadius:20, borderTopRightRadius:20, borderBottomRightRadius:20, padding:2}}>
        <p style={styles.helpText}>In the context of retirement planning and using the Retirement Budget Calculator, asset conversions typically refer to the process of converting one type of retirement account into another, such as:</p>
        <ol style={styles.helpText}>
          <li><b>Roth Conversions</b> : This involves converting funds from a traditional IRA or other pre-tax retirement accounts into a Roth IRA. This conversion is a taxable event, meaning the amount converted is added to your taxable income for the year.</li>
          <br/>
          <li><b>Selling a Primary Residence</b> : If you sell your primary residence, you are essentially converting the proceeds from the sale into a liquid investment account. This can be part of a strategy to use those proceeds for retirement expenses.</li>
        </ol>
        <p style={styles.helpText}>These conversions can be strategic moves to optimize your retirement plan, but they come with tax implications and other considerations. It's important to evaluate your specific situation and consult with a financial advisor or tax professional to ensure these conversions align with your retirement goals.</p>
      </Box>
    }

    return (
      help
    )
  }

  const renderLock = () => {
    if (isLocked) {
      let lockMsg = model.getLock(model.id, "*")
      if (!lockMsg) {
        lockMsg = model.getLock(assetConversion.id, "*")
      }
      return (
        <Box display="flex" justifyContent="flex-start" alignItems="center" mt={1}>
          <LockIcon sx={{color: theme.palette.primary.main, mr:1}}/>
          <Typography variant="body2" color={theme.palette.primary.main}>
            {lockMsg}
          </Typography>
        </Box>
      )
    } else {
      return null
    }
  }

  let srcErrorText
  if (srcAsset && srcAsset.archived && !assetConversionModel.archived) {
    srcErrorText = "Asset is Archived"
  } else if (srcAsset && srcAsset.netWorthExcluded) {
    srcErrorText = "Asset is Excluded from Net Worth — pick a different account or turn off the exclusion"
  }

  let dstErrorText
  if (dstAsset && dstAsset.archived && !assetConversionModel.archived) {
    dstErrorText = "Asset is Archived"
  } else if (dstAsset && dstAsset.netWorthExcluded) {
    dstErrorText = "Asset is Excluded from Net Worth — pick a different account or turn off the exclusion"
  }

  let maxMoney = 1000000000
  let instructions
  if (conversionType === AssetConversionType.QCD) {
    instructions = "Schedule a future Qualified Charitable Distribution. Owner must be 70½ the year of the distribution and amount maximum is $100,000."
    maxMoney = AssetConversion.MaxQCD
  } else if (conversionType === AssetConversionType.RothConversion) {
    instructions = "Schedule a future Roth IRA Conversion."
  } else {
    instructions = "Schedule future IRA to Roth conversions, 401k rollovers, property sales, qualified charitable contributions or other conversions between assets."
  }

  return (
    <ModelEditDialog title="Edit Asset Conversion" open={isOpen}
                     size={showHelp ? "md" : "xs"}
                     onCancel={handleClose}
                     onSave={!isLocked ? handleSave : undefined}
                     onDelete={!isLocked ? handleDelete : undefined}
                     confirmDelete={false}
                     archived={archived}
                     onArchive={onArchive && !isLocked ? handleArchive : undefined}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={showHelp ? 6 : 12}>
          <DialogContentText>
            <Box display="flex" justifyContent="stretch" alignItems="flex-start">
              <Box display="flex" flexGrow={1} pr={1}>
                {instructions}
              </Box>
              <Box display="flex" flexGrow={0} justifyContent="flex-end" width={30} pl={1}>
                <IconButton size="small" sx={{padding:0}}
                            onClick={(event: any) => {
                              setShowHelp(!showHelp)
                            }}
                >
                  <Tooltip title="Ask Finn">
                  <img src={showHelp ? ChatbotOpenImg : ChatbotCloseImg} alt="chatbot"
                       height={30} width={30} style={{borderRadius:"15px"}}/>
                  </Tooltip>
                </IconButton>
              </Box>
            </Box>
          </DialogContentText>
            {renderLock()}
            <TextFieldValidator
              margin="normal"
              name="description"
              label="Title"
              type="text"
              fullWidth
              variant="standard"
              required
              disabled={isLocked}
              validators={{ required: true }}
              value={description}
              onChange={(event: any) => setDescription(event.target.value)}
            />
            <TextFieldValidator
              type="text"
              validators={{ required: true }}
              required
              name="conversionType"
              label="Conversion Type"
              variant="standard"
              autocompleteOptions={{
                freeSolo: false,
                value: conversionType,
                disabled: disabled || isLocked,
                options: Object.values(AssetConversionType),
                getOptionLabel: (option: string) => option ? humanizeString(option) : null,
                onChange: (event: any, value: AssetConversionType, reason: any) => {
                  setConversionType(value)
                }
              }}
            />
            <TextFieldValidator
              type="text"
              validators={{ required: true }}
              required
              name="asset"
              label="Source Asset"
              variant="standard"
              helperText={srcErrorText}
              error={srcErrorText !== undefined}
              autocompleteOptions={{
                freeSolo: false,
                options: srcAssets,
                getOptionLabel: (option: Asset) => option ? option.description : "",
                isOptionEqualToValue: (option: Asset, value: Asset) => option.id === value.id,
                value: srcAsset || null,
                disabled: disabled || isLocked,
                onChange: (event: any, value: Asset, reason: any) => {
                  setSrcAsset(value)
                  let dst = dstAsset
                  if (value) {
                    setAmount(numberToMoneyFormat(value.balance, 0))
                    if (conversionType === AssetConversionType.QCD) {
                      setIncomeType(IncomeType.None)
                      setNonTaxableAmount(numberToMoneyFormat(0, 0))
                    } else {
                      if (value.assetCategory === AssetCategory.LiquidInvestableAssets) {
                        // Check if dest asset is convertable to
                        if (dst && !value.assetTypeDef.convertableTo.includes(dst.assetType)) {
                          setDstAsset(undefined)
                          dst = undefined
                        }
                        if (value.assetTypeDef.isTaxableIRA && (!dst || !dst.assetTypeDef.isTaxableIRA)) {
                          setNonTaxableAmount(numberToMoneyFormat(0, 0))
                          setIncomeType(IncomeType.TaxableIRADistribution)
                        } else if (value.assetType === AssetType.Brokerage) {
                          setIncomeType(IncomeType.ShortTermCapitalGainsOrLoss)
                        } else {
                          setNonTaxableAmount(numberToMoneyFormat(0, 0))
                          setIncomeType(IncomeType.None)
                        }
                      } else if (value.assetCategory === AssetCategory.RealEstateAndProperty) {
                        setNonTaxableAmount(numberToMoneyFormat(0, 0))
                        setIncomeType(IncomeType.LongTermCapitalGainsOrLoss)
                      }
                    }
                  }
                }
              }}
            />
          <Visible cond={conversionType !== AssetConversionType.QCD}>
            <TextFieldValidator
              type="text"
              validators={{ required: conversionType !== AssetConversionType.QCD}}
              required
              name="asset"
              label="Destination Asset"
              variant="standard"
              helperText={dstErrorText}
              error={dstErrorText !== undefined}
              autocompleteOptions={{
                freeSolo: false,
                options: getDestinations(srcAsset),
                getOptionLabel: (option: Asset) => option ? option.description : "",
                isOptionEqualToValue: (option: Asset, value: Asset) => option.id === value.id,
                value: dstAsset || null,
                disabled: disabled || isLocked,
                onChange: (event: any, value: Asset, reason: any) => {
                  setDstAsset(value)
                  if (srcAsset && value) {
                    if (srcAsset.assetCategory === AssetCategory.LiquidInvestableAssets) {
                      if (srcAsset.assetTypeDef.isTaxableIRA && (!value.assetTypeDef.isTaxableIRA)) {
                        setNonTaxableAmount(numberToMoneyFormat(0, 0))
                        setIncomeType(IncomeType.TaxableIRADistribution)
                      } else if (srcAsset.assetType === AssetType.Brokerage) {
                        setIncomeType(IncomeType.ShortTermCapitalGainsOrLoss)
                      } else {
                        setNonTaxableAmount(numberToMoneyFormat(0, 0))
                        setIncomeType(IncomeType.None)
                      }
                    }
                  }
                }
              }}
            />
          </Visible>
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <Visible cond={isAmount}>
              <TextFieldValidator
                type="string"
                validators={{ required: isAmount, isMoney: true, minMoney: 0, maxMoney: maxMoney }}
                name="amount"
                variant="standard"
                margin="dense"
                fullWidth
                label={renderAmountLabel()}
                value={amount}
                disabled={disabled || isLocked}
                onChange={(event: any) => {
                  const value = event.target.value === "" ? "$0" : event.target.value
                  setAmount(value)
                  if (srcAsset && srcAsset.assetTypeDef.isTaxableIRA)
                    setNonTaxableAmount("$0")
                }}
              />
            </Visible>
            <Visible cond={!isAmount}>
              <TextFieldValidator
                type="string"
                validators={{ required: !isAmount, isInt: true, minValue: 0, maxValue: 100 }}
                name="percent"
                variant="standard"
                margin="dense"
                fullWidth
                label={renderAmountLabel()}
                value={percent}
                disabled={disabled || isLocked}
                onChange={(event: any) => {
                  const value = event.target.value === "" ? "0" : event.target.value
                  setPercent(value)
                  // if (srcAsset && srcAsset.assetTypeDef.isTaxableIRA)
                  //   setTaxableAmount(event.target.value)
                }}
              />
            </Visible>
            <FormGroupSpacer/>
            <TextFieldValidator
              type="number"
              validators={{ minValue: minYear, isInt: true }}
              name="year"
              variant="standard"
              margin="dense"
              fullWidth
              label="Year"
              value={year}
              disabled={disabled || isLocked}
              onChange={(event: any) => setYear(event.target.value)}
            />
          </Box>
          <TextFieldValidator
            type="text"
            validators={{ required: true }}
            required
            name="incomeType"
            label="Taxable Income Type"
            variant="standard"
            // styleProp={styles.halfWidthAutoComplete}
            fullWidth
            autocompleteOptions={{
              freeSolo: false,
              options: incomeTypes,
              getOptionLabel: (option: number) => {
                const incomeTypeDef = getIncomeTypeDef(option)
                return(incomeTypeDef.label)
              },
              value: incomeType,
              disabled: disabled || isLocked || conversionType === AssetConversionType.QCD,
              onChange: (event: any, value: number, reason: any) => {
                setIncomeType(value)
                setNonTaxableAmount("0")
              }
            }}
          />
          {incomeType !== IncomeType.None &&
            <TextFieldValidator
              type="string"
              validators={{ required: true, isMoney: true }}
              name="nonTaxableAmount"
              variant="standard"
              margin="dense"
              label="Tax Excludes"
              value={nonTaxableAmount}
              styleProp={styles.halfWidth}
              disabled={disabled || isLocked}
              onChange={(event: any) => setNonTaxableAmount(event.target.value)}
            />
          }
          {!completedAt &&
            <FormControlLabel control={
              <Switch checked={commit}
                      disabled={disabled || Number(year) > minYear}
                      onChange={(event: any) => {
                        setCommit(event.target.checked)
                      }}
              />}
              label={Number(year) >= minYear ? `Complete (${year} or later)` : `Complete the asset conversion on Save.`}
              sx={{marginTop:1}}
            />
          }
          {completedAt &&
            <FormControlLabel control={
              <Switch checked={commit} disabled
              />}
              label={`Completed on ${completedAt}`}
              sx={{marginTop:1}}
            />
          }
        </Grid>
        {showHelp &&
          <Grid item xs={12} sm={showHelp ? 6 : 12}>
            {renderHelp()}
          </Grid>
        }
      </Grid>
    </ModelEditDialog>
  )
}

export default AssetConversionEditDialog